// src/pages/profile-settings/patient/Insurance.jsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import supabase from "@/services/supabaseClient";

function isBypassAuth() {
  const bypassFlag = String(import.meta.env.VITE_BYPASS_AUTH || "").toLowerCase().trim();
  if (bypassFlag !== "true" && bypassFlag !== "1") return false;
  if (!import.meta.env.DEV) return false;

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local");

  return isLocal;
}

const DEV_KEY = "afrimed:dev:patient_insurance";

async function getStableUser() {
  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) throw sessionErr;

  let session = sessionData?.session || null;
  const now = Math.floor(Date.now() / 1000);
  const exp = session?.expires_at || 0;

  if (!session || exp - now < 60) {
    const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
    if (!refreshErr && refreshed?.session) {
      session = refreshed.session;
    }
  }

  if (session?.user) return session.user;

  const {
    data: { user },
    error: userErr,
  } = await supabase.auth.getUser();
  if (userErr) throw userErr;
  return user || null;
}

export default function Insurance() {
  const nav = useNavigate();
  const BYPASS = isBypassAuth();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [provider, setProvider] = React.useState("");
  const [membershipNumber, setMembershipNumber] = React.useState("");
  const [planType, setPlanType] = React.useState("");

  const [notice, setNotice] = React.useState(""); // neutral (no red)
  const [saved, setSaved] = React.useState(false);
  const [autoReturnAfterSave, setAutoReturnAfterSave] = React.useState(false);

  const location = useLocation();

  const hasAny = Boolean(
    String(provider || "").trim() ||
    String(membershipNumber || "").trim() ||
    String(planType || "").trim()
    );

// whoever navigated here can pass { state: { returnTo: location.pathname } }
const returnTo = location.state?.returnTo;

// Back behavior: prefer explicit returnTo, else history back
const goBack = React.useCallback(() => {
  if (typeof returnTo === "string" && returnTo.length > 0) {
    nav(returnTo);
  } else {
    nav(-1);
  }
}, [nav, returnTo]);

const handleSavedClose = React.useCallback(() => {
  setSaved(false);
  if (autoReturnAfterSave) {
    setAutoReturnAfterSave(false);
    goBack();
  }
}, [autoReturnAfterSave, goBack]);

  // Load
  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setNotice("");

        // DEV BYPASS: localStorage, no session required
        if (BYPASS) {
          try {
            const raw = localStorage.getItem(DEV_KEY);
            if (raw) {
              const j = JSON.parse(raw);
              if (!cancelled) {
                setProvider(String(j?.provider || ""));
                setMembershipNumber(String(j?.membership_number || ""));
                setPlanType(String(j?.plan_type || ""));
              }
            }
          } catch {
            // silent
          }
          return;
        }

        // Normal mode: requires session
        const user = await getStableUser();
        if (!user) {
          if (!cancelled) {
            setNotice("Please sign in to add your health insurance details.");
          }
          return;
        }

        const { data, error } = await supabase
          .from("patient_profiles")
          .select("insurance_provider, insurance_policy_number, insurance_member_id, insurance_plan")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (!cancelled && data) {
          setProvider(String(data.insurance_provider || ""));
          setMembershipNumber(
            String(data.insurance_member_id || data.insurance_policy_number || "")
          );
          setPlanType(String(data.insurance_plan || ""));
        }
      } catch {
        // silent failure (no red explosions)
        if (!cancelled) setNotice("We couldn’t load insurance details right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [BYPASS]);

  async function handleSave() {
    if (saving) return;

    // DEV BYPASS: persist locally
    if (BYPASS) {
      try {
        localStorage.setItem(
          DEV_KEY,
          JSON.stringify({
            provider,
            membership_number: membershipNumber,
            plan_type: planType,
            updated_at: new Date().toISOString(),
          })
        );
      } catch {
        // silent
      }
      setAutoReturnAfterSave(true);
      setSaved(true);
      return;
    }

    try {
      setSaving(true);
      setNotice("");

      const user = await getStableUser();
      if (!user) {
        setNotice("Please sign in to save your health insurance details.");
        return;
      }

      const payload = {
        user_id: user.id,
        insurance_provider: String(provider || "").trim() || null,
        insurance_policy_number: String(membershipNumber || "").trim() || null,
        insurance_plan: String(planType || "").trim() || null,
        insurance_member_id: String(membershipNumber || "").trim() || null,
      };

      const { error } = await supabase.from("patient_profiles").upsert(payload, {
        onConflict: "user_id",
      });

      if (error) throw error;

      setAutoReturnAfterSave(true);
      setSaved(true);
    } catch (error) {
      console.error("Insurance save failed", error);
      setNotice("We couldn’t save your insurance details right now.");
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
  if (saving) return;

  // Clear UI immediately (feels snappy)
  setProvider("");
  setMembershipNumber("");
  setPlanType("");

  // DEV BYPASS: remove local cache
  if (BYPASS) {
    try { localStorage.removeItem(DEV_KEY); } catch {}
    setSaved(true);
    return;
  }

  try {
    setSaving(true);
    setNotice("");

    const user = await getStableUser();
    if (!user) {
      setNotice("Please sign in to update your health insurance details.");
      return;
    }

    const { error } = await supabase
      .from("patient_profiles")
      .upsert(
        {
          user_id: user.id,
          insurance_provider: null,
          insurance_policy_number: null,
          insurance_plan: null,
          insurance_member_id: null,
        },
        { onConflict: "user_id" }
      );

    if (error) throw error;

    setSaved(true);
  } catch (error) {
    console.error("Insurance clear failed", error);
    setNotice("We couldn’t clear your insurance details right now.");
  } finally {
    setSaving(false);
  }
}

  return (
    <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter title="Health insurance" onBack={goBack} />

        <Card className="mt-6 overflow-hidden">
          {/* Neutral notice (no red) */}
          {!!notice && (
            <>
              <div className="px-4 py-3 text-body-medium text-on-surface-var">
                {notice}
              </div>
              {/* divider aligned to content */}
              <div className="h-px bg-[var(--Schemes-Outline-Variant,#E6E7EB)] mx-4" />
            </>
          )}

          <div className="px-4 py-4">
            <div className="text-label-medium text-on-surface-var">Provider</div>
            <input
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="e.g. Discovery Health"
              className="mt-1 w-full bg-transparent outline-none text-body-large text-on-surface placeholder:text-on-surface-var"
              disabled={loading}
            />
          </div>

          {/* divider aligned to content */}
          <div className="h-px bg-[var(--Schemes-Outline-Variant,#E6E7EB)] mx-4" />

          <div className="px-4 py-4">
            <div className="text-label-medium text-on-surface-var">Membership number</div>
            <input
              value={membershipNumber}
              onChange={(e) => setMembershipNumber(e.target.value)}
              placeholder="e.g. ABC1234567"
              className="mt-1 w-full bg-transparent outline-none text-body-large text-on-surface placeholder:text-on-surface-var"
              disabled={loading}
            />
          </div>

          {/* divider aligned to content */}
          <div className="h-px bg-[var(--Schemes-Outline-Variant,#E6E7EB)] mx-4" />

          <div className="px-4 py-4">
            <div className="text-label-medium text-on-surface-var">Plan type</div>
            <input
              value={planType}
              onChange={(e) => setPlanType(e.target.value)}
              placeholder="e.g. Classic / Comprehensive"
              className="mt-1 w-full bg-transparent outline-none text-body-large text-on-surface placeholder:text-on-surface-var"
              disabled={loading}
            />
          </div>

          {/* divider aligned to content */}
          <div className="h-px bg-[var(--Schemes-Outline-Variant,#E6E7EB)] mx-4" />

          <div className="px-4 py-4 text-body-medium text-on-surface-var">
            Add your provider and membership details to speed up claims and billing.
          </div>
        </Card>

        {hasAny && (
        <Button
            variant="ghost"
            className="h-12 mt-3 w-full"
            onClick={handleClear}
            disabled={loading || saving}
        >
            Clear
        </Button>
        )}

        <div className="mt-6 flex gap-3">
          <Button variant="ghost" className="flex-1 h-12" onClick={goBack}>
            Back
            </Button>

          <Button className="flex-1 h-12" onClick={handleSave} disabled={loading || saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>

        <Snackbar open={saved} onClose={handleSavedClose} duration={1800}>
          Changes saved
        </Snackbar>
      </div>
    </div>
  );
}
